import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, TextField, Divider, Checkbox, FormControlLabel, Icon, IconButton, Collapse, RadioGroup, Radio, Switch } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Link, useHistory } from 'react-router-dom';

import Loader from '../../common/loader';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get, post } from '../../utils/api-services';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    divider: {
        marginBottom: 24
    },
    box: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadius: 8,
        borderColor: '#cfcfcf',
        paddingLeft: 15,
        paddingRight: 15,
        marginBottom: 15,
        backgroundColor: '#fff'
    },
    text: {
        fontSize: '1.4em',
        color: '#525f7f',
        fontWeight: 'bold'
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    addPermissionsBtn: {
        ...themeColors.greenBtn
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: 20
    },
    cancel: {
        ...themeColors.grayBtn
    },
    store: {
        marginLeft: 20,
        ...themeColors.blueBtn
    },
    tableContainer: {
        width: '100%'
    }
}));

const defModule = { id: 0, name: 'Choose a module' };
const defSubmodule = { id: 0, name: 'Choose a submodule' };

const PolicyCreate = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const history = useHistory();
    const [state, setState] = useState({
        loading: true,
        modules: [],
        submodules: [],
        form: {
            name: '',
            description: '',
            effect: 'allow',
            module: defModule,
            selectModule: false
        },
        detail: [],
        tempForm: {
            visible: true,
            submodule: defSubmodule
        },
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Create policy | IAM';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/iamPolicy/create');
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                modules: response?.modules,
                submodules: response?.submodules
            }));
        } else {
            console.log('[PolicyCreate] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const getModuleActions = async id => {
        const response = await get(`/iamPolicy/${id}/getModuleActions`);
        if (response.status === 200) {
            return response.data;
        } else {
            console.log('[PolicyCreate] getModuleActions error', response);
            helpers.failRequest(response, showToast, signOut);
        }

        return null;
    }

    const getSubmoduleActions = async id => {
        const response = await get(`/iamPolicy/${id}/getSubmoduleActions`);
        if (response.status === 200) {
            return response.data;
        } else {
            console.log('[PolicyCreate] getSubmoduleActions error', response);
            helpers.failRequest(response, showToast, signOut);
        }

        return null;
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, form: { ...prev.form, [target.name]: target.value } }));

    const handleAutocompleteModule = async value => {
        const detail = state.detail.slice();

        if (Number(value?.id)) {
            showLoader(true);
            const submodules = await getModuleActions(value.id);
            showLoader(false);

            if (submodules !== null && submodules?.length) {
                submodules.map(submodule => {
                    if (detail.find(item => item.id === submodule.id) === undefined) detail.push(submodule);
                });
            }

            setState(prev => ({ ...prev, detail, form: { ...prev.form, module: defModule } }));
        } else {
            setState(prev => ({ ...prev, form: { ...prev.form, module: defModule } }));
        }
    }

    const handleAutocompleteSubmodule = async value => {
        const detail = state.detail.slice();

        if (detail.find(item => item?.id === value?.id) === undefined && Number(value?.id)) {
            showLoader(true);
            const actions = await getSubmoduleActions(value.id);
            showLoader(false);

            if (actions !== null) detail.push({ ...value, open: true, allList: false, actions });

            setState(prev => ({ ...prev, detail, tempForm: { visible: false, submodule: defSubmodule } }));
        } else {
            setState(prev => ({ ...prev, tempForm: { visible: true, submodule: defSubmodule } }));
        }
    }

    const handleChangeCheckbox = (id, target) => {
        const detail = state.detail.slice();
        let allList = true;

        detail.map(submodule => {
            if (submodule.id === id) {
                submodule.actions?.map(action => {
                    if (action.name === target.name) action.checked = target.checked;

                    if (!action.checked) allList = false;

                    return action;
                });

                submodule.allList = allList;
            }

            return submodule;
        });

        setState(prev => ({ ...prev, detail }));
    }

    const toggleAllList = (id, target) => {
        const detail = state.detail.slice();
        const allList = target.checked;

        detail.map(submodule => {
            if (submodule.id === id) {
                submodule.actions?.map(action => {
                    action.checked = allList

                    return action;
                });

                submodule.allList = allList;
            }

            return submodule;
        });

        setState(prev => ({ ...prev, detail }));
    }

    const toggleSubmodule = id => {
        const detail = state.detail.slice();

        detail.map(submodule => {
            if (submodule.id === id) {
                submodule.open = !submodule.open;
            }

            return submodule;
        });

        setState(prev => ({ ...prev, detail }));
    }

    const removeSubmodule = id => {
        const detail = state.detail.slice();

        const index = detail.findIndex(submodule => submodule?.id === id);
        detail.splice(index, 1);

        setState(prev => ({ ...prev, detail }));
    }

    const addPermission = () => {
        setState(prev => ({ ...prev, tempForm: { visible: true, submodule: defSubmodule } }));
    }

    const validateStore = () => {
        const { name } = state.form;
        const regex = /^[a-zA-Z0-9+=,.@-_]+$/;
        let validAction = false;

        if (!name.trim()) {
            showToast('Name field is required', 'error');
            return false;
        }
        if (!regex.test(name)) {
            showToast(`Name field: use alphanumeric and '+=,.@-' characters`, 'error', 6000);
            return false;
        }

        state.detail.map(submodule => {
            submodule?.actions?.map(action => {
                if (action.checked) validAction = true;
            });
        });
        if (validAction === false) {
            showToast('Specify at least one action for the selected services', 'error', 6500);
            return false;
        }

        return true;
    }

    const store = async () => {
        const cond = validateStore();
        if (cond) {
            showLoader(true);
            const response = await post('/iamPolicy/store', { ...state.form, detail: state.detail });
            if (response.status === 200) {
                showToast(response.message);
                history.push('/iam/policies');
            } else {
                console.log('[PolicyCreate] store error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <h2>{'Create policy'}</h2>

            <Grid container>
                <Grid item xs={12} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Name'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Name'
                                name='name'
                                value={state.form.name}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Effect'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <RadioGroup row name='effect' value={state.form.effect} onChange={handleChange}>
                                <FormControlLabel value={'allow'} control={<Radio color='primary' />} label='Allow' />
                                <FormControlLabel value={'deny'} control={<Radio color='primary' />} label='Deny' />
                            </RadioGroup>
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={12} lg={9}>
                    <FormGroup>
                        <FormLabel>{'Description'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Description'
                                name='description'
                                value={state.form.description}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <Divider className={classes.divider} />

            <Grid container>
                <Grid item xs={8} lg={4}>
                    <FormGroup>
                        <FormLabel>{'This field preload all its submodules'}</FormLabel>
                        <Autocomplete
                            disabled={!state.form.selectModule}
                            options={state.modules}
                            value={state.form.module}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option?.id === value?.id}
                            onChange={(e, value) => handleAutocompleteModule(value)}
                            renderInput={params => <TextField {...params} placeholder='Module' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={4} lg={3}>
                    <FormGroup>
                        <FormLabel>{'~'}</FormLabel>
                        <FormControlLabel
                            control={<Switch checked={state.form.selectModule} onChange={() => setState(prev => ({ ...prev, form: { ...prev.form, selectModule: !prev.form.selectModule } }))} />}
                            label='Select module'
                        />
                    </FormGroup>
                </Grid>
            </Grid>

            <Divider className={classes.divider} />

            {
                state.detail.map(submodule =>
                    <div key={submodule?.id} className={classes.box} style={{ paddingBottom: 15 }}>
                        <div className={classes.row}>
                            <span className={classes.text}>{submodule?.name}</span>
                            <div>
                                <IconButton onClick={() => removeSubmodule(submodule?.id)}>
                                    <Icon>{'delete'}</Icon>
                                </IconButton>
                                <IconButton onClick={() => toggleSubmodule(submodule?.id)}>
                                    <Icon>{submodule?.open ? 'expand_less' : 'expand_more'}</Icon>
                                </IconButton>
                            </div>
                        </div>

                        <Collapse in={submodule?.open}>

                            <FormLabel>{'Access level'}</FormLabel>
                            <br />
                            <FormControlLabel
                                label='All list actions'
                                control={<Checkbox color='primary' checked={submodule?.allList} name={submodule?.name} onChange={({ target }) => toggleAllList(submodule?.id, target)} />}
                            />
                            <Grid container style={{ paddingLeft: 24 }}>
                                {
                                    submodule?.actions?.map(action =>
                                        <Grid item key={action?.idiam_submodule_action} xs={6} lg={4}>
                                            <FormControlLabel
                                                control={<Checkbox color='primary' checked={action?.checked} name={action?.name} onChange={({ target }) => handleChangeCheckbox(submodule?.id, target)} />}
                                                label={action?.name}
                                            />
                                        </Grid>
                                    )
                                }
                            </Grid>

                        </Collapse>
                    </div>
                )
            }

            {
                state.tempForm.visible &&
                <div className={classes.box} style={{ padding: 15 }}>

                    <span className={classes.text}>{'Select a submodule'}</span>
                    <br /><br />
                    <Grid container>
                        <Grid item xs={12} lg={4}>
                            <FormLabel>{'Submodule'}</FormLabel>
                            <Autocomplete
                                options={state.submodules}
                                value={state.tempForm.submodule}
                                getOptionLabel={option => option?.name ?? ''}
                                getOptionSelected={(option, value) => option?.id === value?.id}
                                onChange={(e, value) => handleAutocompleteSubmodule(value)}
                                renderInput={params => <TextField {...params} placeholder='Submodule' />}
                            />
                        </Grid>
                    </Grid>

                </div>
            }

            <div>
                <Button
                    variant='contained'
                    className={classes.addPermissionsBtn}
                    onClick={addPermission}
                >
                    {'Add permissions'}
                </Button>
            </div>

            <div className={classes.controls}>
                <Button
                    variant='contained'
                    className={classes.cancel}
                    component={Link}
                    to={'/iam/policies'}
                >
                    {'Cancel'}
                </Button>

                <Button
                    variant='contained'
                    className={classes.store}
                    onClick={store}
                >
                    {'Store'}
                </Button>

            </div>
        </div>
    );
}

export default PolicyCreate;